import { forwardRef, type ButtonHTMLAttributes, useState } from "react";
import { cn } from "@/lib/utils";
import { AppleIcon } from "@/components/icons/Icons";
import { useNotification } from "@/hooks/useNotification";
import { useTranslation } from "@/hooks/useTranslation";
import { LoadingButton } from "@/components/ui/loading-button";
import { signIn } from "@/lib/auth-client";
import { PATHS } from "@/config/paths";

export interface AppleAuthButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  isRegister?: boolean;
  className?: string;
}

export const AppleAuthButton = forwardRef<HTMLButtonElement, AppleAuthButtonProps>(
  ({ className, isRegister, ...props }, ref) => {
    const { showNotification } = useNotification();
    const { t } = useTranslation();
    const [isLoading, setIsLoading] = useState(false);

    return (
      <LoadingButton
        {...props}
        data-umami-event="apple-auth-button"
        loading={isLoading}
        spinnerClassName="absolute left-3"
        autoFocus
        className={cn(
          "relative flex w-full items-center rounded-lg border px-3 py-2.5 text-md transition-all",
          "border-gray-300 bg-white text-[#211E1F] hover:bg-[#211E1F]/5 active:bg-muted",
          "dark:border-transparent dark:bg-black dark:text-white dark:hover:bg-white/10 dark:active:bg-muted",
          className,
        )}
        ref={ref}
        onClick={async () => {
          try {
            setIsLoading(true);
            await signIn.social({
              provider: "apple",
              errorCallbackURL: PATHS.AUTH_ERROR,
              callbackURL: PATHS.DASHBOARD,
              newUserCallbackURL: PATHS.NEW_USER,
            });
          } catch (error) {
            showNotification({
              message: t("auth.buttons.apple_signin_failed"),
              type: "error",
            });
          } finally {
            setIsLoading(false);
          }
        }}
        type="button"
      >
        {!isLoading && <AppleIcon className="absolute left-3" />}
        <span className="flex-1 text-center">
          {isRegister ? t("auth.buttons.continue_with_apple") : t("auth.buttons.login_with_apple")}
        </span>
      </LoadingButton>
    );
  },
);
